import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable, OnDestroy, inject } from '@angular/core';
import { ButtonsBarService, KeyValueDTO, ViewState } from '@ms/core';
import { logRequest } from 'app/main/shared/http-utils';
import { Observable, Subject, first, of, switchMap, tap } from 'rxjs';
import { LocationType, SchoolLocation } from '../school-location/model/school-location.model';
import { SchoolLocationStore } from '../store/location.store';
import { SchoolService } from './school.service';

@Injectable({
    providedIn: 'root',
})
export class SchoolLocationService implements OnDestroy {
    private _subjectNew = new Subject<Event>();
    baseUrl = '/school-service/api/location';
    private buttonBarService = inject(ButtonsBarService);
    private http = inject(HttpClient);
    private schoolService = inject(SchoolService);
    private store = inject(SchoolLocationStore);

    getAll(schoolId: string): Observable<SchoolLocation[]> {
        if (!this.store.loaded()) {
            return this.http
                .get<SchoolLocation[]>(`/school-service/api/school/${schoolId}/location`)
                .pipe(tap(result => this.store.set(result)));
        } else {
            return of(this.store.findAll()).pipe(first());
        }
    }

    getAllActive(schoolId: string): Observable<SchoolLocation[]> {
        if (!this.store.loaded()) {
            return this.getAll(schoolId).pipe(tap(result => result.filter(l => l.active)));
        } else {
            return of(this.store.getAllActive()).pipe(first());
        }
    }

    getByTypeActive(schoolId: string, type: LocationType): Observable<SchoolLocation[]> {
        if (!this.store.loaded()) {
            return this.getAll(schoolId).pipe(tap(result => result.filter(l => l.active && l.type == type)));
        } else {
            return of(this.store.getByTypeActive(type)).pipe(first()).pipe(first());
        }
        //return this.http.get<SchoolLocation[]>(`/school-service/api/school/${schoolId}/location/${type}/type`);
    }

    getListForSelection(schoolId: string, type: LocationType): Observable<KeyValueDTO[]> {
        if (!this.store.loaded()) {
            return this.getAll(schoolId).pipe(
                switchMap(result =>
                    of(result.filter(l => l.active && l.type == type).map(l => new KeyValueDTO(l.id, l.name))).pipe(
                        first()
                    )
                )
            );
        } else {
            return of(this.store.getByTypeActive(type).map(l => new KeyValueDTO(l.id, l.name))).pipe(first());
        }
    }

    getById(id: string): Observable<SchoolLocation> {
        if (!this.store.loaded()) {
            return this.getAll(this.schoolService.getActiveSchool().id).pipe(
                switchMap(() => of(this.store.get(id)).pipe(first()))
            );
        } else {
            return of(this.store.get(id)).pipe(first());
        }
    }

    getSchoolLocationsAutocomplete(text: string): Observable<KeyValueDTO[]> {
        if (!this.store.loaded()) {
            return this.getAll(this.schoolService.getActiveSchool().id).pipe(
                switchMap(() => of(this.store.getSchoolLocationsAutocomplete(text)).pipe(first()))
            );
        } else {
            return of(this.store.getSchoolLocationsAutocomplete(text)).pipe(first());
        }
    }

    create(location: SchoolLocation): Observable<HttpResponse<SchoolLocation>> {
        return this.http
            .post<SchoolLocation>(this.baseUrl, location, { observe: 'response', headers: logRequest() })
            .pipe(tap(result => this.store.create(result.body)));
    }

    update(location: SchoolLocation): Observable<HttpResponse<SchoolLocation>> {
        return this.http
            .put<SchoolLocation>(this.baseUrl, location, { observe: 'response', headers: logRequest() })
            .pipe(tap(result => this.store.update(result.body)));
    }

    delete(id: string): Observable<HttpResponse<any>> {
        return this.http
            .delete(`${this.baseUrl}/${id}`, { observe: 'response', headers: logRequest() })
            .pipe(tap(() => this.store.remove(id)));
    }

    // New
    doNew(event: Event) {
        this._subjectNew.next(event);
        this.buttonBarService.changeViewState(ViewState.NEW);
    }

    get new() {
        return this._subjectNew;
    }

    ngOnDestroy() {
        // Clean subscriptions, intervals, etc
        this._subjectNew.unsubscribe();
    }

    map(location: SchoolLocation): SchoolLocation {
        const result: SchoolLocation = Object.assign({}, location);
        return result;
    }

    setActive(id: string, isActive: boolean) {
        return this.http
            .patch(`${this.baseUrl}/${id}/active/${isActive}`, { headers: logRequest() })
            .pipe(tap(() => this.store.setActive(id, isActive)));
    }
}
